<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <v-row align="center" class="mb-4">
      <v-col cols="8" lg="auto">
        <report-food-select-food-companies
          :items="companiesWithFoods"
        ></report-food-select-food-companies>
      </v-col>
      <v-col cols="4" lg="auto">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>

      <v-col cols="12" lg="auto" class="py-0">
        <v-checkbox
          hide-details
          v-model="$store.state.report.filters.showCompanyNames"
          :label="$t('show_company_names_print')"
        ></v-checkbox>
      </v-col>
    </v-row>
    <v-data-table
      :items="customSearch(foods, headers, filters.search)"
      :headers="getMobileHeaders(headers)"
      :loading="loading"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
    >
      <template v-slot:item.num="{ item, index }">
        <!-- This will now correctly apply to food items only, as total is in body.append -->
        {{ index + 1 }}
      </template>
      <template v-slot:item.meal_name="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-if="item" :title="'ID: ' + item.id" v-bind="attrs" v-on="on">
              {{ toLatin(item.meal_name) }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template v-slot:body.append>
        <tr style="font-weight: bold">
          <td v-if="!device.isMobile"></td>
          <!-- Corresponds to 'num' column -->
          <td>{{ $t("totalU") }}</td>
          <!-- Corresponds to 'meal_name' column -->
          <td v-if="selectedShifts.includes(1)">{{ foodTotalsSummary.shift_one }}</td>
          <td v-if="selectedShifts.includes(2)">{{ foodTotalsSummary.shift_two }}</td>
          <td v-if="selectedShifts.includes(3)">{{ foodTotalsSummary.shift_three }}</td>
          <td v-if="selectedShifts.includes(4)">{{ foodTotalsSummary.shift_four }}</td>
          <td>{{ foodTotalsSummary.quantity }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasReloadWatch from "@/mixins/report/hasReloadWatch";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import ReportFoodSelectFoodCompanies from "@/components/report/fields/ReportFieldSelectFoodCompanies.vue";

export default {
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasReloadWatch, hasMobileHeaders],
  components: {
    ReportFoodSelectFoodCompanies,
  },
  data() {
    return {
      search: "",
      foodByOrders: [],
      report: [],
      loading: false,
      selectedShifts: [1, 2, 3, 4],
    };
  },
  computed: {
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("m"), value: "meal_name" },
      ];

      const shiftHeaders = [];
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("1st_shift"), value: "shift_one" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("2nd_shift"), value: "shift_two" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("3rd_shift"), value: "shift_three" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("4th_shift"), value: "shift_four" });
      }

      return [
        ...baseHeaders,
        ...shiftHeaders,
        { text: this.$t("total"), value: "quantity" },
      ];
    },

    filters() {
      return this.$store.state.report.filters;
    },
    foods() {
      if (!this.report || this.report.length === 0) {
        return [];
      }

      const aggregatedMeals = this.report
        .filter((order) => {
          return (
            this.selectedCompanies.some((company) => company.id === order.company_id) &&
            this.selectedShifts.includes(order.shift)
          );
        })
        .reduce((acc, meal) => {
          const mealName = meal.meal_name;
          const quantity = meal.quantity || 0; // Ensure quantity is a number

          if (!acc[mealName]) {
            acc[mealName] = {
              id: meal.food_id,
              meal_name: mealName,
              shift_one: 0,
              shift_two: 0,
              shift_three: 0,
              shift_four: 0,
              quantity: 0,
            };
          }

          if (meal.shift === 1 && this.selectedShifts.includes(1)) {
            acc[mealName].shift_one += quantity;
          } else if (meal.shift === 2 && this.selectedShifts.includes(2)) {
            acc[mealName].shift_two += quantity;
          } else if (meal.shift === 3 && this.selectedShifts.includes(3)) {
            acc[mealName].shift_three += quantity;
          } else if (meal.shift === 4 && this.selectedShifts.includes(4)) {
            acc[mealName].shift_four += quantity;
          }
          acc[mealName].quantity += quantity;

          return acc;
        }, {});

      return Object.values(aggregatedMeals).map((meal, index) => ({
        ...meal,
        num: index + 1,
      }));
    },
    companiesWithFoods() {
      if (!this.report || this.report.length === 0) {
        return [];
      }
      const companyIds = this.report.map((item) => item.company_id);
      return [...new Set(companyIds)];
    },
    foodTotalsSummary() {
      return this.foods.reduce(
        (acc, food) => {
          acc.shift_one += food.shift_one || 0;
          acc.shift_two += food.shift_two || 0;
          acc.shift_three += food.shift_three || 0;
          acc.shift_four += food.shift_four || 0;
          acc.quantity += food.quantity || 0;
          return acc;
        },
        {
          shift_one: 0,
          shift_two: 0,
          shift_three: 0,
          shift_four: 0,
          quantity: 0,
        }
      );
    },
  },
  methods: {
    fetchFoods() {
      this.loading = true;
      reportService
        .getFoodOrdersOrdered(this.$store.state.restaurant.id, {
          date: this.filters.dates.from,
          date2: this.filters.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.fetchFoods();
      },
      immediate: true,
      deep: true,
    },
    isPrinting() {
      if (this.isPrinting) {
        const totalCompanies = this.$store.state.report.foodCompanies.length;
        const selectedCount = this.selectedCompanies.length;
        const allSelected = totalCompanies === selectedCount;

        // Subtitle with company count
        const companySummary = allSelected
          ? this.$t("for_all_companies")
          : `${this.$t("selected_companies")} (${selectedCount} ${this.$t(
              "of"
            )} ${totalCompanies})`;

        // Footer with company names
        let companiesFooter = "";
        if (this.filters.showCompanyNames) {
          const companyNames = this.selectedCompanies
            .map((company) => this.toLatin(company.name))
            .join(", ");
          companiesFooter = `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
            <strong>${this.$t("companies")}:</strong> ${companyNames}
          </div>`;
        }

        const itemsForPrint = [
          ...this.foods,
          {
            num: "",
            meal_name: this.$t("totalU"),
            shift_one: this.foodTotalsSummary.shift_one,
            shift_two: this.foodTotalsSummary.shift_two,
            shift_three: this.foodTotalsSummary.shift_three,
            shift_four: this.foodTotalsSummary.shift_four,
            quantity: this.foodTotalsSummary.quantity,
          },
        ];
        this.printDocument({
          tables: [{ headers: this.headers, items: itemsForPrint, showCaption: true }],
          title: [this.$t("report") + ": ", this.$t("meals_main"), this.subtitle].join(
            " "
          ),
          subtitle: `<h4 style="text-align: center">${companySummary}</h4>`,
          afterTableHtml: companiesFooter,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const itemsForExcel = [
          ...this.foods,
          {
            num: "", // Or a specific marker like '*'
            meal_name: this.$t("totalU"),
            shift_one: this.foodTotalsSummary.shift_one,
            shift_two: this.foodTotalsSummary.shift_two,
            shift_three: this.foodTotalsSummary.shift_three,
            shift_four: this.foodTotalsSummary.shift_four,
            quantity: this.foodTotalsSummary.quantity,
          },
        ];
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("meals_main")].join(" ") + ".xlsx",
          sheets: [
            {
              name: this.$t("meals_main"),
              headers: this.headers,
              items: itemsForExcel,
              title: [this.$t("report"), this.$t("meals_main")].join(" "),
              subtitle: this.subtitle,
            },
            {
              name: this.$t("companies"),
              headers: [
                { text: this.$t("id"), value: "id" },
                { text: this.$t("company"), value: "company" },
              ],
              items: this.selectedCompanies,
            },
          ],
        });
      }
    },
  },
};
</script>
